import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff, PhoneAndroid, Email } from "@mui/icons-material";
import { login, verifyMobileLogin, generateReturnCode } from "../../api/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    generateExternalLoginURL,
} from "../../config/externalLogin";
import config from "../../config/config";
import Logo from "../../assests/logo.png";

const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showUsernamePasswordForm, setShowUsernamePasswordForm] = useState(false);
    const [error, setError] = useState(""); // For displaying error messages
    const navigate = useNavigate();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    useEffect(() => {
        const token = localStorage.getItem("token");

        if (token) {
            console.log("Token found, navigating to /home...");
            navigate("/home", { replace: true });
        }
    }, []);

    const handleLogin = async () => {
        try {
            const { return_code } = await generateReturnCode();
            sessionStorage.setItem("ivs_return_code", return_code);
            // Redirect URI points to backend - it validates Referer then redirects to frontend
            const redirectURI = `${config.API_BASE_URL}/ivs-callback?flow=admin-login`;
            const loginURL = generateExternalLoginURL("SMS", redirectURI, return_code);
            window.location.href = loginURL;
        } catch (err) {
            console.error("Failed to generate return code:", err);
            Swal.fire({ icon: "error", title: "Error", text: "Could not start login. Please try again." });
        }
    };

    const handleLoginEmail = async () => {
        try {
            const { return_code } = await generateReturnCode();
            sessionStorage.setItem("ivs_return_code", return_code);
            // Redirect URI points to backend - it validates Referer then redirects to frontend
            const redirectURI = `${config.API_BASE_URL}/ivs-callback?flow=admin-login`;
            const loginURL = generateExternalLoginURL("EMAIL", redirectURI, return_code);
            window.location.href = loginURL;
        } catch (err) {
            console.error("Failed to generate return code:", err);
            Swal.fire({ icon: "error", title: "Error", text: "Could not start login. Please try again." });
        }
    };

    const handleSubmitLogin = async () => {
        if (!username || !password) {
            setError("Username and Password are required");
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Username and Password are required",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const response = await login({ username, password });

            if (response.data.success === true) {
                // Store token and user info in localStorage
                localStorage.setItem("token", response.data.token || response.data.user.id);
                localStorage.setItem(
                    "user",
                    JSON.stringify(response.data.user)
                );
                localStorage.setItem("branch_id", response.data.user.branch_id);
                localStorage.setItem("client_id", response.data.user.client_id);
                localStorage.setItem(
                    "branch_name",
                    response.data.user.branch_name
                );

                // Show success message
                Swal.fire({
                    icon: "success",
                    title: "Login Successful!",
                    text: `Welcome back, ${response.data.user.user_name}!`,
                    confirmButtonColor: "#28a745",
                    confirmButtonText: "Continue",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: true,
                }).then(() => {
                    // Navigate to home page after success
                    window.location.href = "/home";
                });
            } else {
                setError("Invalid username or password");
                Swal.fire({
                    icon: "error",
                    title: "Login Failed",
                    text: "Invalid username or password",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Try Again",
                });
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Handle unauthorized response
                setError("Invalid username or password");
                Swal.fire({
                    icon: "error",
                    title: "Authentication Failed",
                    text: "Invalid username or password. Please check your credentials.",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Try Again",
                });
            } else {
                setError("An error occurred. Please try again.");
                console.error("Error:", error);
                Swal.fire({
                    icon: "error",
                    title: "Connection Error",
                    text: "An error occurred while connecting to the server. Please try again.",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Retry",
                });
            }
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, #e8eef4 0%, #f0f4f8 50%, #e6ecf2 100%)",
                p: 2,
            }}
        >
            {/* Login Card */}
            <Box
                sx={{
                    width: { xs: "100%", sm: "420px" },
                    maxWidth: "100%",
                    p: 4,
                    bgcolor: "white",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                    borderRadius: 4,
                    textAlign: "center",
                }}
            >
                {/* Header / Logo */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <img
                        src={Logo}
                        alt="Logo"
                        style={{
                            width: 160,
                            height: 160,
                            objectFit: "contain",
                            flexShrink: 0,
                        }}
                    />
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "1.75rem",
                                lineHeight: 1.2,
                                textAlign: "center",
                                color: "#2563eb", // solid blue to match buttons
                            }}
                        >
                            <Box component="span" sx={{ display: "block" }}>Admin</Box>
                            <Box component="span" sx={{ display: "block" }}>easyQue</Box>
                        </Typography>
                    </Box>
                </Box>

                <Typography variant="body1" sx={{ mb: 3, color: "#6b7280" }}>
                    Welcome back! Please login to continue.
                </Typography>

                {/* Sign in with Mobile Number - Blue Button */}
                {/* <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<PhoneAndroid sx={{ color: "white" }} />}
                    onClick={handleLogin}
                    sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 600,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: "#2563eb",
                        "&:hover": { bgcolor: "#1d4ed8" },
                    }}
                >
                    Sign in with Mobile Number
                </Button> */}

                {/* Sign in with Email - Solid Color Button */}
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Email sx={{ color: "white" }} />}
                    onClick={handleLoginEmail}
                    sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 600,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: "#7c3aed",
                        "&:hover": {
                            bgcolor: "#6d28d9",
                        },
                    }}
                >
                    Sign in with Email
                </Button>

                {/* Username and Password Fields */}
                {showUsernamePasswordForm && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <TextField
                            label="Username"
                            variant="outlined"
                            fullWidth
                            sx={{ mb: 2 }}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                        />
                        <TextField
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            fullWidth
                            sx={{ mb: 2 }}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                borderRadius: 2,
                                bgcolor: "#22c55e",
                                "&:hover": { bgcolor: "#16a34a" },
                            }}
                            onClick={handleSubmitLogin}
                        >
                            Submit
                        </Button>
                    </Box>
                )}

                {/* Sign in with Username/Password - Link */}
                <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Typography
                        component="span"
                        onClick={() => setShowUsernamePasswordForm(!showUsernamePasswordForm)}
                        sx={{
                            color: "#2563eb",
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "#1d4ed8" },
                        }}
                    >
                        {showUsernamePasswordForm ? "Hide Username/Password Login" : "Sign in with Username & Password"}
                    </Typography>
                </Box>

                {/* Registration Prompt - Bordered Box */}
                <Box
                    sx={{
                        mt: 3,
                        pt: 3,
                        borderTop: "1px solid #e5e7eb",
                        p: 2,
                        borderRadius: 2,
                        border: "1px solid #93c5fd",
                        bgcolor: "rgba(147, 197, 253, 0.06)",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                        Don't have an account?
                    </Typography>
                    <Typography
                        component="span"
                        onClick={() => navigate("/free-trial")}
                        sx={{
                            color: "#2563eb",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "#1d4ed8" },
                        }}
                    >
                        Sign up for free trial
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1, rowGap: 0.3, mt: 1, pl: 2 }}>
                        {[
                            "30-day free trial",
                            "No credit card required",
                            "Unlimited branches",
                            "Unlimited customers/day",
                            "QR code queue",
                            "Real-time display",
                            "Advanced analytics",
                            "CRM integration",
                            "Priority support",
                            "Custom branding",
                        ].map((text) => (
                            <Typography key={text} variant="caption" sx={{ color: "#6b7280", display: "flex", alignItems: "center", gap: 0.5, textAlign: "left" }}>
                                <Box component="span" sx={{ color: "#22c55e", fontWeight: 700, fontSize: "0.85rem" }}>&#10003;</Box>
                                {text}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;
